import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  useRsvpQuestions,
  useRsvpAnswers,
  useCreateRsvpQuestion,
  useUpdateRsvpQuestion,
  useDeleteRsvpQuestion,
} from '../../hooks/useRsvpQuestions';
import type { RsvpQuestionWithAnswers } from '../../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

type QuestionType = 'text' | 'select' | 'multiselect';

interface QuestionFormState {
  question: string;
  type: QuestionType;
  options: string[];
  required: boolean;
}

const defaultFormState: QuestionFormState = {
  question: '',
  type: 'text',
  options: [],
  required: false,
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function RsvpQuestionsManager() {
  const { eventId } = useParams<{ eventId: string }>();

  // Queries
  const { data: questions, isLoading, error } = useRsvpQuestions(eventId);
  const { data: answersData } = useRsvpAnswers(eventId);

  // Mutations
  const createQuestion = useCreateRsvpQuestion(eventId!);
  const updateQuestion = useUpdateRsvpQuestion(eventId!);
  const deleteQuestion = useDeleteRsvpQuestion(eventId!);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [formState, setFormState] = useState<QuestionFormState>(defaultFormState);
  const [newOption, setNewOption] = useState('');

  // Answers preview dialog
  const [answersDialogOpen, setAnswersDialogOpen] = useState(false);
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const openCreateDialog = () => {
    setEditingQuestionId(null);
    setFormState(defaultFormState);
    setNewOption('');
    setDialogOpen(true);
  };

  const openEditDialog = (q: RsvpQuestionWithAnswers) => {
    setEditingQuestionId(q.id);
    setFormState({
      question: q.question,
      type: q.type as QuestionType,
      options: Array.isArray(q.options) ? (q.options as string[]) : [],
      required: q.required,
    });
    setNewOption('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!eventId) return;

    const data = {
      question: formState.question,
      type: formState.type,
      options: formState.type !== 'text' ? formState.options : undefined,
      required: formState.required,
    };

    if (editingQuestionId) {
      await updateQuestion.mutateAsync({ questionId: editingQuestionId, data });
    } else {
      const maxSortOrder = questions?.reduce(
        (max, q) => Math.max(max, q.sortOrder),
        -1,
      ) ?? -1;
      await createQuestion.mutateAsync({ ...data, sortOrder: maxSortOrder + 1 });
    }

    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    await deleteQuestion.mutateAsync(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const handleMoveUp = async (q: RsvpQuestionWithAnswers, index: number) => {
    if (!questions || index === 0) return;
    const prevQ = questions[index - 1]!;
    await Promise.all([
      updateQuestion.mutateAsync({
        questionId: q.id,
        data: { sortOrder: prevQ.sortOrder },
      }),
      updateQuestion.mutateAsync({
        questionId: prevQ.id,
        data: { sortOrder: q.sortOrder },
      }),
    ]);
  };

  const handleMoveDown = async (q: RsvpQuestionWithAnswers, index: number) => {
    if (!questions || index === questions.length - 1) return;
    const nextQ = questions[index + 1]!;
    await Promise.all([
      updateQuestion.mutateAsync({
        questionId: q.id,
        data: { sortOrder: nextQ.sortOrder },
      }),
      updateQuestion.mutateAsync({
        questionId: nextQ.id,
        data: { sortOrder: q.sortOrder },
      }),
    ]);
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFormState((prev) => ({
        ...prev,
        options: [...prev.options, newOption.trim()],
      }));
      setNewOption('');
    }
  };

  const removeOption = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const openAnswersPreview = (questionId: string) => {
    setPreviewQuestionId(questionId);
    setAnswersDialogOpen(true);
  };

  // ─── Loading / Error ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load RSVP questions
      </Alert>
    );
  }

  // Find preview question data
  const previewQuestionAnswers = answersData?.find(
    (a) => a.questionId === previewQuestionId,
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Custom RSVP Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add custom questions that guests will see when RSVPing
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Add Question
        </Button>
      </Stack>

      {/* Questions list */}
      {!questions || questions.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              No custom questions yet. Add a question to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {questions.map((q, index) => (
            <Card key={q.id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {q.question}
                      </Typography>
                      {q.required && (
                        <Chip label="Required" size="small" color="error" variant="outlined" />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={q.type}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {q._count.answers} answer{q._count.answers !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                    {Array.isArray(q.options) && (q.options as string[]).length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {(q.options as string[]).map((opt) => (
                          <Chip key={opt} label={opt} size="small" />
                        ))}
                      </Stack>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveUp(q, index)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveDown(q, index)}
                      disabled={index === questions.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openAnswersPreview(q.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openEditDialog(q)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirmId(q.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingQuestionId ? 'Edit Question' : 'Add Question'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Question"
              value={formState.question}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, question: e.target.value }))
              }
              fullWidth
              required
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formState.type}
                label="Type"
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    type: e.target.value as QuestionType,
                    options: e.target.value === 'text' ? [] : prev.options,
                  }))
                }
              >
                <MenuItem value="text">Text (free response)</MenuItem>
                <MenuItem value="select">Select (single choice)</MenuItem>
                <MenuItem value="multiselect">Multi-select (multiple choices)</MenuItem>
              </Select>
            </FormControl>

            {/* Options for select / multiselect */}
            {formState.type !== 'text' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Options
                </Typography>
                <Stack spacing={1}>
                  {formState.options.map((opt, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {opt}
                      </Typography>
                      <IconButton size="small" onClick={() => removeOption(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="New option"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      size="small"
                      fullWidth
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                    <Button variant="outlined" onClick={addOption} disabled={!newOption.trim()}>
                      Add
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formState.required}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, required: e.target.checked }))
                  }
                />
              }
              label="Required"
            />

            {/* Preview */}
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Preview
            </Typography>
            <Card variant="outlined">
              <CardContent>
                {formState.type === 'text' && (
                  <TextField
                    label={formState.question || 'Your question here'}
                    required={formState.required}
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    disabled
                  />
                )}
                {formState.type === 'select' && (
                  <FormControl fullWidth size="small" disabled>
                    <InputLabel>{formState.question || 'Your question here'}</InputLabel>
                    <Select
                      value=""
                      label={formState.question || 'Your question here'}
                    >
                      {formState.options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {formState.type === 'multiselect' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {formState.question || 'Your question here'}
                      {formState.required ? ' *' : ''}
                    </Typography>
                    {formState.options.map((opt) => (
                      <FormControlLabel
                        key={opt}
                        control={<Checkbox size="small" disabled />}
                        label={opt}
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !formState.question.trim() ||
              (formState.type !== 'text' && formState.options.length === 0) ||
              createQuestion.isPending ||
              updateQuestion.isPending
            }
          >
            {createQuestion.isPending || updateQuestion.isPending
              ? 'Saving...'
              : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question? All associated answers will
            also be deleted. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteQuestion.isPending}
          >
            {deleteQuestion.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Answers Preview Dialog */}
      <Dialog
        open={answersDialogOpen}
        onClose={() => setAnswersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Answers: {previewQuestionAnswers?.question ?? 'Loading...'}
        </DialogTitle>
        <DialogContent>
          {previewQuestionAnswers ? (
            previewQuestionAnswers.answers.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No answers yet
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {previewQuestionAnswers.answers.map((a, i) => (
                  <Card key={i} variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2">{a.answer}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Guest: {a.guestId.slice(0, 8)}...
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
